import { Palette, RotateCcw } from "lucide-react";

// Default accent per template (must match backend defaults — used as the
// placeholder/reset target; we never send a default unless the user picked one).
const DEFAULT_ACCENTS = {
  modern: "#0040E0",
  classic: "#1E3A8A",
  compact: "#4B5563",
  two_column: "#047857",
};

export default function AccentColors({ templates, accents, onChange }) {
  if (!templates?.length) return null;

  const update = (key, value) => {
    const next = { ...(accents || {}) };
    if (value) next[key] = value;
    else delete next[key];
    onChange(next);
  };

  return (
    <div data-testid="accent-colors-panel">
      <div className="overline mb-3">brand · template accents</div>
      <h3 className="font-heading text-xl font-bold tracking-tight mb-2 flex items-center gap-2">
        <Palette className="w-5 h-5 text-[var(--brand-accent)]" strokeWidth={1.75} />
        TEMPLATE ACCENT COLORS
      </h3>
      <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-4 max-w-2xl">
        Tint each template&apos;s section headers with your brand colour. Leave blank
        to keep the template&apos;s built-in accent.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {templates.map((t) => {
          const fallback = DEFAULT_ACCENTS[t.key] || `#${t.accent}`;
          const current = accents?.[t.key] || "";
          const effective = current || fallback;
          return (
            <div
              key={t.key}
              className="border border-[var(--border-default)] p-3 bg-white"
              data-testid={`accent-card-${t.key}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-heading font-bold text-sm">{t.label}</span>
                {current && (
                  <button
                    type="button"
                    onClick={() => update(t.key, "")}
                    title="Reset to template default"
                    className="text-[var(--text-muted)] hover:text-[var(--brand-primary)]"
                    data-testid={`accent-reset-${t.key}`}
                  >
                    <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <label
                  className="relative shrink-0 w-9 h-9 border border-[var(--border-default)] cursor-pointer overflow-hidden"
                  style={{ background: effective }}
                  title="Pick colour"
                >
                  <input
                    type="color"
                    value={effective}
                    onChange={(e) => update(t.key, e.target.value)}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                    data-testid={`accent-picker-${t.key}`}
                  />
                </label>
                <input
                  type="text"
                  value={current}
                  onChange={(e) => update(t.key, e.target.value)}
                  placeholder={fallback}
                  spellCheck={false}
                  className="swiss-input !py-1.5 !px-2 text-xs font-mono"
                  data-testid={`accent-hex-${t.key}`}
                />
              </div>
              <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--text-muted)] mt-2">
                {current ? "custom" : `default · ${fallback}`}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
